/**
 * ADDRESS THUNKS — Async thunks per add/update/remove di un Address di
 * un Business. Dopo ogni mutazione, dispatch del fetch del Business
 * owner per rinfrescare il Redux state (che contiene `addresses` come
 * array denormalizzato dentro Business).
 *
 * Oggi owner = solo Business: il thunk chiama direttamente
 * `businessAddressApi` senza routing polimorfico. Se in futuro il
 * backend supportera' altri owner per Address, si sostituira' con un
 * pattern a istradamento per-className come emailAddressThunks/phoneThunks.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { businessAddressApi } from '../business/businessAddressApi';
import type {
  Address,
  CreateAddressPayload,
  UpdateAddressPayload,
  RemoveAddressPayload,
} from './addressTypes';
import { fetchBusiness } from '../business/businessThunks';
import type { AppDispatch } from '../../store/store';
import { extractError } from '../../store/thunkHelpers';

export const addAddress = createAsyncThunk<Address, CreateAddressPayload>(
  'addresses/add',
  async ({ businessId, ...body }, { dispatch, rejectWithValue }) => {
    try {
      const created = await businessAddressApi.create(businessId, body);
      (dispatch as AppDispatch)(fetchBusiness(businessId));
      return created;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const updateAddress = createAsyncThunk<Address, UpdateAddressPayload>(
  'addresses/update',
  async ({ businessId, addressId, ...body }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await businessAddressApi.update(businessId, addressId, body);
      (dispatch as AppDispatch)(fetchBusiness(businessId));
      return updated;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const removeAddress = createAsyncThunk<number, RemoveAddressPayload>(
  'addresses/remove',
  async ({ businessId, addressId }, { dispatch, rejectWithValue }) => {
    try {
      await businessAddressApi.remove(businessId, addressId);
      (dispatch as AppDispatch)(fetchBusiness(businessId));
      return addressId;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);
